import React from 'react';
import { Avatar, Chip, Link } from '@heroui/react';
import { RefShareOrder, TrafficSource } from '../../../../api/services';
import {
  OnlyFansIc,
  InstagramIc,
  TwitterIc,
  GoogleIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  IcAnotherSoc,
  GlobalIc,
} from '../../../../components/icons';

interface CampaignCellProps {
  order: RefShareOrder;
}

const getSourceIcon = (source: string, size: number = 14) => {
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    reddit: <IcReddit size={size} />,
    instagram: <InstagramIc size={size} fill="#A8A8B0" />,
    tiktok: <IcTikTok size={size} />,
    google: <GoogleIc size={size} fill="#A8A8B0" />,
    telegram: <IcTg size={size} />,
    twitter: <TwitterIc size={size} fill="#A8A8B0" />,
    youtube: <IcYoutube size={size} />,
    another: <IcAnotherSoc size={size} />,
  };
  return iconMap[source?.toLowerCase()] || <GlobalIc size={size} />;
};

const getSourceLabel = (source: string) => {
  const labelMap: Record<string, string> = {
    only_fans: 'OnlyFans',
    reddit: 'Reddit',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    telegram: 'Telegram',
    twitter: 'Twitter',
    youtube: 'YouTube',
    another: 'Other',
  };
  return labelMap[source?.toLowerCase()] || source;
};

const CampaignCell: React.FC<CampaignCellProps> = ({ order }) => {
  const model = order.model || {};
  const modelAvatar = model.avatar;
  const fullTrackLinkId = model.track_link_id ? String(model.track_link_id) : '';
  const shortId = fullTrackLinkId.length > 4 ? fullTrackLinkId.slice(0, 4) : fullTrackLinkId;
  const modelCode = shortId ? `ON${shortId}` : '';
  const modelUsername = model.of_user_name || '';
  const topPerformer = model.overall_popularity_percent || 0;

  const source = order.source || '';
  const freeTrialAccess = order.offer?.free_trial_access || false;
  const campaignName = order.name || '';
  const campaignType = order.type || 'free';

  const trafficType = freeTrialAccess ? 'Free-Trial' : 'По ссылке';
  const subscriptionType = campaignType === 'free' ? 'FREE' : campaignType === 'free trial' ? 'Free-Trial' : 'FREE';

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="flex items-start gap-[16px] py-2">
      <Avatar
        src={modelAvatar}
        alt={model.name || modelUsername}
        className="w-8 h-8 shrink-0 rounded-[8px]"
        radius="sm"
      />

      <div className="flex flex-col gap-[4px]">
        {/* Row 1: Model code + username + TOP performer + subscription type */}
        <div className="flex items-center gap-[8px] flex-wrap">
          <span className="text-[14px] font-medium text-[#11181c] leading-[20px]">
            {model.name}
          </span>

          {modelUsername && (
            <Link
              href={`https://onlyfans.com/${modelUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[14px] text-[#006fee]"
            >
              @{modelUsername}
            </Link>
          )}

          {topPerformer > 0 && (
            <Chip
              className="bg-[rgba(245,165,36,0.2)] text-[#f5a524] rounded-full px-[4px] h-[24px]"
            >
              <span className="text-[12px] leading-[16px]">{topPerformer}%</span>
            </Chip>
          )}

          <Chip
            className="bg-[rgba(0,111,238,0.2)] text-[#006fee] rounded-full px-[4px] h-[24px]"
          >
            <span className="text-[12px] leading-[16px]">{subscriptionType}</span>
          </Chip>
        </div>

        {/* Row 2: Source + date + campaign name + source badge + traffic type */}
        <div className="flex items-center gap-[8px] flex-wrap">
          {/* Purple date/source chip */}
          {source && order.created_at && (
            <div className="bg-[rgba(120,40,200,0.2)] rounded-full px-[8px] py-[0px] h-[24px] flex items-center justify-center">
              <span className="text-[#7828c8] text-[12px] font-normal leading-[16px]">
                {campaignName ? `${campaignName}` : getSourceLabel(source)} {formatDate(order.created_at)}
              </span>
            </div>
          )}

          {/* White bordered platform chip */}
          {source && (
            <div className="border border-[#D4D4D8] rounded-[14px] px-[8px] py-[0px] h-[24px] flex items-center gap-[4px]">
              <div className="w-[14px] h-[14px] overflow-hidden">{getSourceIcon(source, 14)}</div>
              <span className="text-[#11181c] text-[12px] font-normal leading-[16px]">
                {getSourceLabel(source)}
              </span>
            </div>
          )}

          {/* Type text */}
          {/* <div className="flex items-center">
            <span className="text-[#11181c] text-[12px] font-semibold leading-[16px]">
              {trafficType}
            </span>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default CampaignCell;
